/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * Application entry point. Wires the three-page flow:
 *   Challenge (email) → Login (6-digit code) → Dashboard (post-auth).
 *
 * Every page renders inside a single `<wa-page>` shell (#234): pre-auth
 * pages mount into a plain shell via `mountInPageShell`; the dashboard
 * swaps in `<td-dashboard>`, which renders its own fully-slotted
 * wa-page.
 *
 * Event-driven page transitions via the typed CustomEvents exported by
 * the page-owning components: `SetupReadyDetail` (TdSetup),
 * `NewVaultPassphraseDetail` (TdPassphrase), `LogoutEventDetail`
 * (TdDashboard).
 *
 * Service-worker registration (#244) is gated behind the
 * `VITE_BD_ENABLE_SW` build flag — see `registerServiceWorker` TSDoc.
 *
 * The auto-start trailer at the bottom of this module is gated by a
 * `__BD_SKIP_MAIN_AUTOSTART__` global flag so the unit-style boot
 * tests can import the module without triggering a real boot. The
 * flag is checked inside the IIFE so production never observes it
 * (production never sets it).
 */

// Import WebAwesome components
import '@awesome.me/webawesome-pro/dist/components/badge/badge.js';
import '@awesome.me/webawesome-pro/dist/components/breadcrumb-item/breadcrumb-item.js';
import '@awesome.me/webawesome-pro/dist/components/breadcrumb/breadcrumb.js';
import '@awesome.me/webawesome-pro/dist/components/button/button.js';
import '@awesome.me/webawesome-pro/dist/components/callout/callout.js';
import '@awesome.me/webawesome-pro/dist/components/card/card.js';
import '@awesome.me/webawesome-pro/dist/components/checkbox/checkbox.js';
import '@awesome.me/webawesome-pro/dist/components/details/details.js';
import '@awesome.me/webawesome-pro/dist/components/dialog/dialog.js';
import '@awesome.me/webawesome-pro/dist/components/drawer/drawer.js';
import '@awesome.me/webawesome-pro/dist/components/icon/icon.js';
import '@awesome.me/webawesome-pro/dist/components/input/input.js';
import '@awesome.me/webawesome-pro/dist/components/option/option.js';
import '@awesome.me/webawesome-pro/dist/components/page/page.js';
import '@awesome.me/webawesome-pro/dist/components/progress-bar/progress-bar.js';
import '@awesome.me/webawesome-pro/dist/components/radio-group/radio-group.js';
import '@awesome.me/webawesome-pro/dist/components/radio/radio.js';
import '@awesome.me/webawesome-pro/dist/components/select/select.js';
import '@awesome.me/webawesome-pro/dist/components/skeleton/skeleton.js';
import '@awesome.me/webawesome-pro/dist/components/tab-group/tab-group.js';
import '@awesome.me/webawesome-pro/dist/components/tab-panel/tab-panel.js';
import '@awesome.me/webawesome-pro/dist/components/tab/tab.js';
import '@awesome.me/webawesome-pro/dist/components/textarea/textarea.js';

/* WebAwesome Styles */
import '@awesome.me/webawesome-pro/dist/styles/webawesome.css';
import '@awesome.me/webawesome-pro/dist/styles/native.css';
import '@awesome.me/webawesome-pro/dist/styles/themes/awesome.css';

import './styles/main.css';
import { TdSetup, type SetupReadyDetail } from './components/TdSetup.js';
//import { TdOnboarding } from './components/TdOnboarding.js';
import './components/TdAccount.js';
import './components/TdAcceptance.js';
import './components/TdReminder.js';
import './components/TdNavigation.js';
import './components/TdNodata.js';
import './components/TdStorylist.js';
import './components/TdPrintQr.js';
import './components/TdVaultLock.js';
import './components/TdForgotPass.js';
import './components/TdStoryAction.js';
import { authService } from './services/auth.js';
import { vaultService } from './services/vault.js';
import { clearElement } from './services/utils.js';
import { initUiLocale } from './services/i18n.js';
import './components/TdDashboard.js';
import { TdLogin, type ChallengeResultWithKey } from './components/TdLogin.js';
import { TdChallenge } from './components/TdChallenge.js';
import type { NewVaultPassphraseDetail } from './components/TdPassphrase.js';

/**
 * Window event dispatched when a new service worker has finished
 * installing and is waiting to take over. `event.detail.registration`
 * carries the `ServiceWorkerRegistration`; pass it to
 * {@link applyServiceWorkerUpdate} (e.g. from an "update available"
 * prompt) to activate the new version and reload.
 */
export const SW_UPDATE_EVENT = 'bd-sw-update-available';

/** Detail payload of the {@link SW_UPDATE_EVENT} window event. */
export interface SwUpdateDetail {
  registration: ServiceWorkerRegistration;
}

/**
 * Register `/service-worker.js` and wire the update flow (#244).
 *
 * Update flow: the worker does NOT skipWaiting on install. When a new
 * version reaches the `installed` state while an old worker is still
 * controlling the page, this function dispatches
 * {@link SW_UPDATE_EVENT} on `window` so UI can prompt the user;
 * {@link applyServiceWorkerUpdate} then completes the handshake.
 * Callers that skip the prompt simply pick up the new worker on the
 * next full restart of the app (all tabs closed).
 *
 * Only wired up when the `VITE_BD_ENABLE_SW` build flag is `'true'`
 * (see the trailer at the bottom of this module).
 *
 * @param swContainer injectable seam for tests; production uses
 *   `navigator.serviceWorker`.
 */
export const registerServiceWorker = async (
  swContainer: ServiceWorkerContainer = navigator.serviceWorker
): Promise<void> => {
  try {
    const registration = await swContainer.register('/service-worker.js');

    const announceUpdate = (): void => {
      window.dispatchEvent(
        new CustomEvent<SwUpdateDetail>(SW_UPDATE_EVENT, {
          detail: { registration },
        })
      );
    };

    // A worker may already be parked in `waiting` from a previous visit.
    if (registration.waiting && swContainer.controller) {
      announceUpdate();
    }

    registration.addEventListener('updatefound', () => {
      const installing = registration.installing;
      installing?.addEventListener('statechange', () => {
        // `installed` + an existing controller means "new version
        // waiting"; without a controller it is the very first install
        // and there is nothing to announce.
        if (installing.state === 'installed' && swContainer.controller) {
          announceUpdate();
        }
      });
    });
  } catch (registrationError) {
    console.error('SW registration failed: ', registrationError);
  }
};

/**
 * Activate a waiting service worker and reload once it takes control.
 * Intended to be called from an "update available" prompt reacting to
 * {@link SW_UPDATE_EVENT}. No-op when nothing is waiting.
 *
 * @param registration the registration announced by the update event
 * @param swContainer injectable seam for tests; production uses
 *   `navigator.serviceWorker`.
 */
export const applyServiceWorkerUpdate = (
  registration: ServiceWorkerRegistration,
  swContainer: ServiceWorkerContainer = navigator.serviceWorker
): void => {
  if (!registration.waiting) {
    return;
  }
  swContainer.addEventListener(
    'controllerchange',
    () => window.location.reload(),
    { once: true }
  );
  registration.waiting.postMessage({ type: 'SKIP_WAITING' });
};

/**
 * Cleanup path for when the build flag is off: drop any service worker
 * (and its caches die with the next version bump) that an earlier
 * build may have registered, so disabling the flag actually disables
 * the PWA instead of freezing users on a stale shell.
 *
 * @param swContainer injectable seam for tests; production uses
 *   `navigator.serviceWorker`.
 */
export const unregisterServiceWorkers = async (
  swContainer: ServiceWorkerContainer = navigator.serviceWorker
): Promise<void> => {
  try {
    const registrations = await swContainer.getRegistrations();
    await Promise.all(
      registrations.map(registration => registration.unregister())
    );
  } catch (unregisterError) {
    console.error('SW unregistration failed: ', unregisterError);
  }
};

// The service-worker wiring lives in the auto-start trailer at the
// bottom of this module so tests importing with
// `__BD_SKIP_MAIN_AUTOSTART__` stay free of registration side effects.

/**
 * Mount `content` inside a fresh `<wa-page>` shell (#234).
 *
 * The shell makes wa-page the single top-level layout owner for every
 * page: pre-auth pages (challenge, login) and the setup pipeline mount
 * into its default (main) slot; the dashboard replaces the container's
 * content with `<td-dashboard>`, whose template renders its own
 * `<wa-page>` complete with header/navigation/aside slots — so exactly
 * one shell owns the layout at any time.
 *
 * `content` goes into the default slot directly (no `<main>` wrapper):
 * the pre-auth hero (`.bd-login-shell`) is full-bleed, and the
 * dashboard-gutter CSS in main.css targets `wa-page > main` only.
 */
const mountInPageShell = (
  appContainer: HTMLElement,
  content: HTMLElement
): void => {
  clearElement(appContainer);
  const shell = document.createElement('wa-page');
  shell.setAttribute('mobile-breakpoint', '868');
  shell.appendChild(content);
  appContainer.appendChild(shell);
};

export const loadLoginPage = async (
  appContainer: HTMLElement,
  challengeResult?: ChallengeResultWithKey
): Promise<void> => {
  document.body.classList.add('bd-login-page');
  const loginComponent = new TdLogin();
  mountInPageShell(appContainer, loginComponent);
  // Lit's first render is microtask-scheduled; the `getForm()` accessor
  // below queries the rendered DOM, so we must await the render to
  // complete before wiring submit handlers — otherwise the form falls
  // through to the browser's default GET handler and the page reloads
  // with `?email=...` in the URL.
  await loginComponent.updateComplete;

  if (challengeResult?.ok) {
    loginComponent.setChallengeResult(challengeResult);
  }

  const form = loginComponent.getForm();
  const errorMessage = loginComponent.getErrorMessage();
  const codeInput = loginComponent.getCodeInput();
  const submitBtn = loginComponent.getSubmitButton();

  if (!form || !errorMessage || !codeInput || !submitBtn) {
    console.error('Required login form elements not found');
    return;
  }

  form.addEventListener('submit', async e => {
    e.preventDefault();
    if (!challengeResult) {
      // Defensive: a logout → login transition lands here without a
      // challenge result (the .js source quietly passed `undefined`
      // through to `challengeLogin`, which would have failed at the
      // identifier-read). Surface and bail.
      console.error(
        'main.ts: login submit without a challenge result — redirecting to challenge page'
      );
      await loadChallengePage(appContainer);
      return;
    }
    const result = await authService.challengeLogin(
      codeInput.value,
      challengeResult,
      errorMessage,
      submitBtn
    );
    if (result) {
      loadDashboardPage(appContainer);
    }
  });
};

export const loadChallengePage = async (
  appContainer: HTMLElement
): Promise<void> => {
  document.body.classList.add('bd-login-page');
  const challengeComponent = new TdChallenge();
  mountInPageShell(appContainer, challengeComponent);
  // See loadLoginPage above — must await Lit's first render before
  // querying the form element, otherwise the submit handler is never
  // wired and the browser falls through to a default GET submission.
  await challengeComponent.updateComplete;

  const form = challengeComponent.getForm();
  if (!form) {
    console.error('Challenge form element not found');
    return;
  }

  const errorMessage = challengeComponent.getErrorMessage();
  if (!errorMessage) {
    console.error('Challenge error message element not found');
    return;
  }

  form.addEventListener('submit', async e => {
    e.preventDefault();
    const result = await authService.formChallenge(form, errorMessage);
    if (result.ok) {
      await loadLoginPage(appContainer, result as ChallengeResultWithKey);
    }
  });
};

export const loadDashboardPage = (appContainer: HTMLElement): void => {
  document.body.classList.remove('bd-login-page');
  const tdSetup = new TdSetup();
  mountInPageShell(appContainer, tdSetup);
  tdSetup.addEventListener('ready', event => {
    const detail = (event as CustomEvent<SetupReadyDetail>).detail;
    console.log('Setup state: ', detail.state);
    if (detail.state === 'ready') {
      // The dashboard brings its own wa-page (header/navigation/aside
      // slots), so it replaces the plain shell instead of nesting in it.
      clearElement(appContainer);
      const dashboard = document.createElement('td-dashboard');
      appContainer.appendChild(dashboard);
      dashboard.addEventListener('logout', () => {
        authService.logout();
        void loadLoginPage(appContainer);
      });
    }
  });

  tdSetup.addEventListener('new-vault-passphrase', event => {
    const detail = (event as CustomEvent<NewVaultPassphraseDetail>).detail;
    console.log('New vault passphrase: ', detail.passphrase);
    void vaultService.createVault(detail.passphrase);
  });
};

export const initApp = async (): Promise<void> => {
  const appContainer = document.getElementById('app');
  if (!appContainer) {
    console.error('main.ts: #app container not found in DOM');
    return;
  }

  // #89: restore the persisted UI locale BEFORE the first page mounts
  // so the initial render is already in the user's language.
  await initUiLocale();

  // #181: `isAuthenticated()` is async — it awaits the refresh on
  // expiry rather than fire-and-forget. Without the await we'd route
  // to the challenge page while a successful refresh was still in
  // flight (the original symptom: "token gets removed very fast").
  if (await authService.isAuthenticated()) {
    loadDashboardPage(appContainer);
  } else {
    // User is not authenticated, show the challenge page (email entry).
    await loadChallengePage(appContainer);
  }
};

/**
 * Wrap `initApp` so a throw inside the async function surfaces as a
 * logged error rather than an unhandled promise rejection —
 * `addEventListener` does not await async listeners, and the
 * synchronous call below would also drop the rejection on the floor
 * without an explicit `.catch`.
 */
export const startApp = (): void => {
  initApp().catch(error => {
    console.error('Failed to initialize app:', error);
  });
};

// Testability seam: a unit test can set `window.__BD_SKIP_MAIN_AUTOSTART__`
// before importing this module to suppress the auto-boot, then call
// `initApp` / `startApp` directly with controlled mocks. Production
// never sets this flag, so the boot runs as before.
declare global {
  interface Window {
    __BD_SKIP_MAIN_AUTOSTART__?: boolean;
  }
}

if (typeof window !== 'undefined' && !window.__BD_SKIP_MAIN_AUTOSTART__) {
  if (document.readyState !== 'loading') {
    startApp();
  } else {
    document.addEventListener('DOMContentLoaded', startApp);
  }

  // #244: service-worker registration is gated behind the
  // VITE_BD_ENABLE_SW build flag (statically replaced by Vite). With
  // the flag off we actively unregister leftovers from earlier builds
  // that shipped an always-on worker, so disabling the flag actually
  // disables the PWA.
  if ('serviceWorker' in navigator) {
    if (import.meta.env.VITE_BD_ENABLE_SW === 'true') {
      window.addEventListener('load', () => void registerServiceWorker());
    } else {
      void unregisterServiceWorkers();
    }
  }
}
